import type { MarkdownBlock, TableBlock, MetricBlock } from '../types/blocks';
import type { AssistantMessage, Block } from '../types';

/**
 * Strips markdown formatting from text to make it suitable for TTS
 * Removes: headers (#), bold (**), italic (*), code (`), links, etc.
 */
export const stripMarkdown = (text: string): string => {
  return (
    text
      // Remove code blocks
      .replace(/```[\s\S]*?```/g, '')
      // Remove inline code
      .replace(/`([^`]+)`/g, '$1')
      // Remove headers
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      // Remove italic
      .replace(/\*([^*]+)\*/g, '$1')
      // Remove strikethrough
      .replace(/~~([^~]+)~~/g, '$1')
      // Remove links but keep text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove images
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
      // Remove horizontal rules
      .replace(/^[-*_]{3,}\s*$/gm, '')
      // Remove blockquotes
      .replace(/^>\s+/gm, '')
      // Clean up multiple spaces and newlines
      .replace(/\s+/g, ' ')
      .trim()
  );
};

/**
 * Formats a number value according to the specified format
 */
const formatMetricValue = (
  value: number,
  format?: string,
  currency?: string,
  precision?: number
): string => {
  const defaultPrecision = precision ?? 2;

  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: defaultPrecision,
        maximumFractionDigits: defaultPrecision
      }).format(value);

    case 'percentage':
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: defaultPrecision,
        maximumFractionDigits: defaultPrecision
      }).format(value / 100);

    case 'decimal':
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: defaultPrecision,
        maximumFractionDigits: defaultPrecision
      }).format(value);

    case 'number':
    default:
      return new Intl.NumberFormat('en-US').format(value);
  }
};

/**
 * Extracts plain text from a MarkdownBlock for TTS
 */
export const extractMarkdownText = (block: MarkdownBlock): string => {
  return stripMarkdown(block.content);
};

/**
 * Extracts text from a TableBlock for TTS
 * Format: "Table: {title}. Columns: {col1}, {col2}, ..."
 */
export const extractTableText = (block: TableBlock): string => {
  const parts: string[] = [];

  if (block.title) {
    parts.push(`Table: ${block.title}.`);
  }

  if (block.columns && block.columns.length > 0) {
    const columnNames = block.columns.map((col) => col.header).join(', ');
    parts.push(`Columns: ${columnNames}.`);
  }

  return parts.join(' ');
};

/**
 * Extracts text from a MetricBlock for TTS
 * Format: "{label}: {formatted_value}"
 */
export const extractMetricText = (block: MetricBlock): string => {
  const formattedValue = formatMetricValue(
    block.value,
    block.format,
    block.currency,
    block.precision
  );
  return `${block.label}: ${formattedValue}`;
};

/**
 * Extracts text from a single block based on its type
 */
const extractBlockText = (block: Block): string => {
  switch (block.type) {
    case 'markdown':
      return extractMarkdownText(block as MarkdownBlock);
    case 'table':
      return extractTableText(block as TableBlock);
    case 'metric':
      return extractMetricText(block as MetricBlock);
    case 'text':
      return (block as any).content || '';
    default:
      return '';
  }
};

/**
 * Extracts all text content from an AssistantMessage
 * Includes message content and all blocks in order
 */
export const extractMessageText = (message: AssistantMessage): string => {
  const textParts: string[] = [];

  // Add message content first (overview message)
  if (message.content) {
    textParts.push(stripMarkdown(message.content));
  }

  // Add text from all response blocks in order
  if (message.response_blocks && message.response_blocks.length > 0) {
    message.response_blocks.forEach((block) => {
      const blockText = extractBlockText(block);
      if (blockText && blockText.trim().length > 0) {
        textParts.push(blockText);
      }
    });
  }

  // Join all parts with a period and space for natural speech pauses
  return textParts.join('. ').trim();
};
